/**
 * Studio Hiring Preferences component (genres and styles they hire for)
 */
import { Card } from "@/components/ui/card";
import { Camera } from "lucide-react";
import { GENRES, SHOOTING_STYLES, EDITING_STYLES, LIGHTING_STYLES } from "@/lib/profileOptions";
import CheckBox from "./CheckBox";

function StyleCheckGrid({ title, description, options, selected, onToggle, testIdPrefix }) {
  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      {description && <p className="text-white/50 mb-6">{description}</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {options.map((item) => {
          const isSelected = selected.includes(item.value);
          return (
            <div
              key={item.value}
              className={`flex items-center gap-3 p-3 bg-white/[0.05] border rounded-sm cursor-pointer transition-all ${isSelected ? "border-white/40 bg-white/5" : "border-white/10"}`}
              onClick={() => onToggle(item.value)}
              data-testid={`${testIdPrefix}-${item.value}`}
            >
              <CheckBox checked={isSelected} />
              <span className="text-sm">{item.label}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function StudioHiringPrefs({
  hiringGenres, setHiringGenres,
  shootingStyles, setShootingStyles,
  editingStyles, setEditingStyles,
  lightingStyles, setLightingStyles
}) {
  const toggle = (list, setter) => (value) => {
    if (list.includes(value)) {
      setter(list.filter(s => s !== value));
    } else {
      setter([...list, value]);
    }
  };

  return (
    <>
      <StyleCheckGrid
        title={<><Camera className="w-5 h-5 inline mr-2" />What We Hire For</>}
        description="Select the types of sessions where you hire second shooters or assistants"
        options={GENRES}
        selected={hiringGenres}
        onToggle={toggle(hiringGenres, setHiringGenres)}
        testIdPrefix="hiring-genre"
      />
      <StyleCheckGrid
        title="Studio Shooting Aesthetic"
        description="What shooting styles define your studio's work?"
        options={SHOOTING_STYLES}
        selected={shootingStyles}
        onToggle={toggle(shootingStyles, setShootingStyles)}
        testIdPrefix="studio-style"
      />
      <StyleCheckGrid
        title="Studio Editing Style"
        description="What editing look does your studio deliver?"
        options={EDITING_STYLES}
        selected={editingStyles || []}
        onToggle={toggle(editingStyles || [], setEditingStyles)}
        testIdPrefix="studio-editing"
      />
      <StyleCheckGrid
        title="Studio Lighting Preferences"
        description="What lighting setups does your studio typically use?"
        options={LIGHTING_STYLES}
        selected={lightingStyles || []}
        onToggle={toggle(lightingStyles || [], setLightingStyles)}
        testIdPrefix="studio-lighting"
      />
    </>
  );
}

export default StudioHiringPrefs;
